// @ts-strict-ignore
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ArrayUtils } from '@shared/util/array-utils';
import { ReplaceLabelingUtils } from './replace-labeling-utils';
import { PropertyUtils } from '@article/util/property-utils';
import { MetaElementBehaviorPropertyDef } from '../../../constants/meta-element-behavior-property-def';
import { ConnectionAreaVO, MetaElementUsageIdentityVO, MetaElementVO, SelectionMetaElementUsageIdentity, TextureAreaVO } from '@shared/vo/meta-element.vo';
import { NumberUtils } from '@shared/util/number-utils';
import { Connection } from '@3d-scene/model3d/connection';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { PropertyVO } from '../../article/model/vo/property.vo';
import { AcceptingConnectionSortGroupHelper } from '@3d-scene/helper/connection/accepting-connection-sort-group.helper';
import { ProjectModel } from '../../../model/project.model';
import { LabelStrip } from './replace-labeling-data';
import { ReplaceLabelingDataBuilder } from './replace-labeling-data-builder';
import { ReplaceLabelingArticleHelper } from './replace-labeling-article.helper';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { UndoRedoUtils } from '@shared/undo/undo-redo-utils';
import { UndoDetachArticlesOperation } from '@shared/undo/operation/undo-detach-articles-operation';
import { SelectionModel } from '../../article/model/selection.model';
import { UndoAttachArticlesOperation } from '@shared/undo/operation/undo-attach-articles-operation';
import { UndoReplaceArticlesByLocationOperation } from '@shared/undo/operation/undo-replace-articles-by-location-operation';
import { SelectionArticleGroupIdentity } from '../../article/model/vo/selection-article-group-identity';
import { ProjectHelper } from '../../../helper/project.helper';
import { LabelingUtils } from '@3d-scene/model3d/util/labeling-utils';
import { TextureConnectionUtils } from '@3d-scene/processor/texture-connection-utils';
import { Injectable } from '@angular/core';
import { InsertArticleUtils } from '../../article/util/insert-article-utils';
import { SelectionModelService } from '../../article/service/selection-model.service';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { IsProjectType } from '../../../model/is-project-type';

@Injectable({ providedIn: 'root' })
export class LabelStripJoiner {
  constructor(
    private projectModel: ProjectModel,
    private detachArticleUsageService: DetachArticleUsageService,
    private selectionModel: SelectionModel,
    private selectionModelService: SelectionModelService,
    private acceptingConnectionSortHelper: AcceptingConnectionSortGroupHelper,
    private undoRedoHelper: UndoRedoHelper,
    private replaceLabelingHelper: ReplaceLabelingArticleHelper,
    private projectHelper: ProjectHelper,
    private undoRedoUtils: UndoRedoUtils,
    private connectionInfoService: ConnectionInfoService,
    private textureConnectionUtils: TextureConnectionUtils,
    private insertArticleUtils: InsertArticleUtils,
    private replaceLabelingUtils: ReplaceLabelingUtils
  ) {}

  public isOperationAvailable(articleContainers: ArticleContainer[]): boolean {
    if (ArrayUtils.isEmpty(articleContainers) || !IsProjectType.mounting(this.projectModel.currentProject)) {
      return false;
    }

    const labelStrips: ArticleContainer[] = articleContainers.filter((container) => this.isSuitableLabelStrip(container));
    if (labelStrips.length !== articleContainers.length || labelStrips.length <= 1) {
      // only suitable label-strips have to be selected
      return false;
    }

    if (!this.isLabelStripsOfTheSameConfiguration(labelStrips)) {
      // it's not possible to merge different label-strip articles or label-strips with different multi-line count
      return false;
    }

    const acceptingConnections: Connection[] = this.collectAcceptingConnectionsToConnectStrip(labelStrips);
    return this.acceptingConnectionSortHelper.splitConnectionCubesOnGroups(acceptingConnections, false).length === 1;
  }

  public async joinLabelStrips(articleContainers: ArticleContainer[]): Promise<void> {
    const labelStripContainers: ArticleContainer[] = articleContainers.filter(container => this.isSuitableLabelStrip(container));
    if (labelStripContainers.length <= 1) {
      return;
    }

    const acceptingConnections: Connection[] = this.collectAcceptingConnectionsToConnectStrip(labelStripContainers);
    const sourceContainer: ArticleContainer = this.getArticleContainerWithMostRecentVersion(labelStripContainers);

    const labelStrip: LabelStrip = new LabelStrip(this.connectionInfoService);
    labelStrip.originalUsageId = sourceContainer.articleGUID;
    labelStrip.articleId = sourceContainer.article.identity;
    labelStrip.articleVersion = sourceContainer.article.version;
    labelStrip.onAdapter = false;
    labelStrip.backwardAcceptingIds = acceptingConnections.map((connection) =>
      MetaElementUsageIdentityVO.create(connection.articleContainer.articleGUID, connection.connectionName)
    );
    for (const labelStripContainer of labelStripContainers) {
      labelStrip.textures.push(...ReplaceLabelingDataBuilder.buildLabelStripTextures(labelStripContainer, false));
    }

    const detachOperation: UndoDetachArticlesOperation = this.undoRedoUtils.createUndoDetachOperationByArticleContainers(labelStripContainers,);
      await this.detachArticleUsageService.detachArticleUsageFromProjectByIds(ArticleContainerUtils.extractArticleGUIDs(labelStripContainers));
    const containers: ArticleContainer[] = await this.replaceLabelingHelper.createLabelingArticles([labelStrip]);
    const attachOperation: UndoAttachArticlesOperation = this.undoRedoUtils.createUndoAttachOperationByArticleContainers(containers,);
    const replaceOperation: UndoReplaceArticlesByLocationOperation = new UndoReplaceArticlesByLocationOperation(
      attachOperation, detachOperation, null, this.insertArticleUtils);
    await this.undoRedoHelper.pushUndoOperation(replaceOperation);
    this.updateSelection(labelStripContainers, containers);
  }

  private getArticleContainerWithMostRecentVersion(articleContainers: ArticleContainer[]): ArticleContainer {
    let resultContainer: ArticleContainer = null;
    for (const articleContainer of articleContainers) {
      if (!resultContainer || articleContainer.article.version > resultContainer.article.version) {
        resultContainer = articleContainer;
      }
    }
    return resultContainer;
  }

  private collectAcceptingConnectionsToConnectStrip(labelStrips: ArticleContainer[]): Connection[] {
    const acceptingConnectionByStrips: Connection[] = this.getAcceptingConnectionsByLabelStrips(labelStrips);
    // find suitable connections like in case when multiple connections get selected on 3D-scene by shift-key
    return this.replaceLabelingHelper.findSuitableConnectionsByCollision(acceptingConnectionByStrips, this.getFeatureProperties(labelStrips[0]));
  }

  private getAcceptingConnectionsByLabelStrips(labelStrips: ArticleContainer[]): Connection[] {
    const acceptingConnections: Connection[] = [];
    for (const labelStrip of labelStrips) {
      const backwardIds: MetaElementUsageIdentityVO[] = ArticleUsageUtils.getBackwardConnectedConnectionUsageIds(labelStrip.articleUsage);
      for (const backwardId of backwardIds) {
        const connection: Connection = this.connectionInfoService.findConnectionByIdentity(backwardId);
        if (connection) {
          acceptingConnections.push(connection);
        }
      }
    }
    return acceptingConnections;
  }

  private isSuitableLabelStrip(articleContainer: ArticleContainer): boolean {
    return (
      this.isSuitableLabelStripType(articleContainer.article) &&
      this.isNonAdapterStrip(articleContainer) &&
      this.isNotComplexArticlePart(articleContainer)
    );
  }

  private isSuitableLabelStripType(article: ArticleVO): boolean {
    return this.replaceLabelingUtils.isLabelStripArticle(article);
  }

  private isNonAdapterStrip(articleContainer: ArticleContainer): boolean {
    return !this.replaceLabelingUtils.isStripOnAdapter(articleContainer);
  }

  private isNotComplexArticlePart(articleContainer: ArticleContainer): boolean {
    return !ArticleUsageUtils.isCompositePartArticleUsage(articleContainer.articleUsage);
  }

  private isLabelStripsOfTheSameConfiguration(labelStrips: ArticleContainer[]): boolean {
    let articleId: number = NaN;
    let multilineCount: number = NaN;
    for (const labelStrip of labelStrips) {
      const labelArticleId: number = labelStrip.article.identity;
      const labelMultilineCount: number = this.getMultilineCount(labelStrip);
      if (NumberUtils.isNotNumber(articleId)) {
        articleId = labelArticleId;
        multilineCount = labelMultilineCount;
      } else if (articleId !== labelArticleId || multilineCount !== labelMultilineCount) {
        return false;
      }
    }

    return true;
  }

  private getMultilineCount(labelStrip: ArticleContainer): number {
    const textureArea: MetaElementVO = labelStrip.article.metaElements.find((metaElement) => metaElement instanceof TextureAreaVO);
    const multilineCount: number = PropertyUtils.getIntPropertyValue(
      textureArea.behaviorProperties,
      MetaElementBehaviorPropertyDef.ALLOWED_MULTILINES
    );
    return NumberUtils.isNotNumber(multilineCount) ? 1 : multilineCount;
  }

  private getFeatureProperties(labelStrip: ArticleContainer): PropertyVO[] {
    const givingConnection: Connection = ConnectionInfoUtils.getFirstGivingConnection(labelStrip);
    return ConnectionInfoUtils.createFeaturePropertyList(givingConnection.metaElementVO as ConnectionAreaVO, labelStrip.article);
  }

  private updateSelection(detachedContainers: ArticleContainer[], attachedContainers: ArticleContainer[]): void {
    if (this.selectionModel.isArticleSelection()) {
      this.updateArticleSelection(detachedContainers, attachedContainers);
    } else if (this.selectionModel.isConnectionSelection()) {
      this.updateConnectionSelection(detachedContainers);
    }
  }

  private updateArticleSelection(detachedContainers: ArticleContainer[], attachedContainers: ArticleContainer[]): void {
    const shouldSelectAttachedContainers: boolean = detachedContainers.some((container: ArticleContainer) => container.articleSelected);
    for (const detachedContainer of detachedContainers) {
      const articleSelectionIdentity: SelectionArticleGroupIdentity = this.projectHelper.createSelection(detachedContainer.articleUsage);
      this.selectionModelService.unSelectObject(articleSelectionIdentity);
    }
    if (shouldSelectAttachedContainers) {
      for (const attachedContainer of attachedContainers) {
        const articleSelectionIdentity: SelectionArticleGroupIdentity = this.projectHelper.createSelection(attachedContainer.articleUsage);
        this.selectionModelService.selectObject(articleSelectionIdentity, true, false);
      }
    }
  }

  private updateConnectionSelection(detachedContainers: ArticleContainer[]): void {
    const oldSelection: any[] = this.selectionModel.selectedObjects.concat();
    const newSelection: MetaElementUsageIdentityVO[] = [];
    this.selectionModelService.removeAllSelections();

    for (const selectedObject of oldSelection) {
      if (selectedObject instanceof MetaElementUsageIdentityVO) {
        const detachedContainer: ArticleContainer = detachedContainers.find(
          (container: ArticleContainer) => container.articleUsage.id === selectedObject.articleUsageId
        );
        const connectionIdentity: MetaElementUsageIdentityVO = detachedContainer
          ? LabelingUtils.findAcceptingAreaIdByRelativeConnection(detachedContainer, selectedObject.metaElementName)
          : selectedObject;
        if (connectionIdentity) {
          const textureConnection: Connection = this.textureConnectionUtils.findTextureConnectionCubeByRelativeAcceptingAreaId(connectionIdentity);
          if (textureConnection) {
            newSelection.push(new SelectionMetaElementUsageIdentity(textureConnection.articleContainer.articleGUID, textureConnection));
          } else {
            newSelection.push(connectionIdentity);
          }
        }
      }
    }

    for (const selectionIdentity of newSelection) {
      this.selectionModelService.selectObject(selectionIdentity, true, false, true);
    }
  }
}
